import {Component, Inject, OnInit, ViewChild} from '@angular/core';
import {Set} from 'immutable';
import {MultiselectOptionItem, UiConstants} from "../../../../../util/core-utils";
import {FormBuilder, FormGroup, NgForm, Validators} from "@angular/forms";
import {Angular2Multiselects} from "../../../../../util/multiselect";
import {ForwardingNgFormRef, LocalFormGroupValidationErrors, QueryResult} from "../../../../../lib/util/services";
import {TranslateService} from "@ngx-translate/core";
import {TaskRecord, TaskRecordService} from "../../../../../lib/task/task-record.service";
import {NgbDatePickerParserFormatter} from "../../../../../util/ngb-datepicker";
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from "@angular/material/dialog";
import {NgbDateStruct} from "@ng-bootstrap/ng-bootstrap";
import {DownloadedFile} from "../../../../../lib/util/downloaded-files";
import {saveAs} from 'file-saver';
import {InvoicePaymentTypeMultiItem} from "../../../../invoicing/invoices/invoice-create/invoice-create-clone.model";
import {Dates} from "../../../../../lib/util/dates";
import {ToasterService} from "../../../../../fork/angular2-toaster/src/toaster.service";
import {TranslateUtils} from "../../../../../util/translate";
import {Currency, CurrencyService} from "../../../../../lib/currency.service";

@Component({
  selector: 'app-task-record-sup-export-dialog',
  templateUrl: './task-record-sup-export-dialog.component.html',
  styleUrls: ['./task-record-sup-export-dialog.component.scss']
})
export class TaskRecordSupExportDialogComponent implements OnInit {

  UiConstants = UiConstants;
  TaskRecordSupExportDialogType = TaskRecordSupExportDialogType;

  @ViewChild('f') form: NgForm;

  formGroup: FormGroup;

  offlineDropdownSettings: Angular2Multiselects.Settings;

  private formGroupValidationErrors: LocalFormGroupValidationErrors;

  paymentTypes: InvoicePaymentTypeMultiItem[] = [];

  currencyCodes: MultiselectOptionItem<string>[] = [];

  model: TaskRecordSupExportModel = new TaskRecordSupExportModel();

  exportInProgress: boolean = false;

  constructor(private translateService: TranslateService,
              private formBuilder: FormBuilder,
              private toasterService: ToasterService,
              private currencyService: CurrencyService,
              private taskRecordService: TaskRecordService,
              private datePickerParserFormatter: NgbDatePickerParserFormatter,
              public dialogRef: MatDialogRef<TaskRecordSupExportDialogComponent, TaskRecordSupExportDialogResult>,
              @Inject(MAT_DIALOG_DATA) public data: TaskRecordSupExportDialogData
  ) {
    this.formGroup = this.createFormGroup(formBuilder);
    this.formGroupValidationErrors = LocalFormGroupValidationErrors.ofForm(
      this.createForwardingHtmlForm(),
      this.formGroup
    );
    this.offlineDropdownSettings = new Angular2Multiselects.SettingsBuilder()
      .singleSelection(true)
      .enableSearchFilter(true)
      .enableCheckAll(false)
      .remoteSearch(false)
      .searchBy(['itemName'])
      .autoPosition(true)
      .translate(true)
      .build();

    this.model.deliveryDate = this.datePickerParserFormatter.fromLocalDate(Dates.today())!;
    this.model.issueDate = this.datePickerParserFormatter.fromLocalDate(Dates.today())!;
  }

  ngOnInit(): void {
    this.initPaymentTypes();
    this.initCurrencies();
  }

  private initPaymentTypes() {
    InvoicePaymentTypeMultiItem.loadItems().subscribe(paymentTypes => {
      this.paymentTypes.push(...paymentTypes);
      this.model.paymentType = [];
      const transfer = this.paymentTypes.find(pt => pt.id === 'TRANSFER');
      if (transfer) {
        this.model.paymentType.push(transfer);
      }
    });
  }

  private initCurrencies() {
    this.currencyService.query({}).subscribe((result: QueryResult<Currency.Currency>) => {
      const array = result.items.toArray();
      array.forEach((currency) => {
        const item = {id: currency.currencyCode, itemName: currency.localizedName};
        this.currencyCodes.push(item);
        if (item.id === 'HUF') {
          this.model.currencyCode = [item];
        }
      });
    });
  }

  public static openDialog(
    dialog: MatDialog,
    data: TaskRecordSupExportDialogData,
    resultCallback: (result?: TaskRecordSupExportDialogResult) => void) {
    if (data.taskRecordIds.size === 0) {
      return;
    }
    const dialogRef = dialog.open(TaskRecordSupExportDialogComponent, {
      closeOnNavigation: true,
      data: data,
      height: 'auto',
      panelClass: 'custom-dialog-container'
    });
    dialogRef.afterClosed().subscribe((result: TaskRecordSupExportDialogResult) => {
      resultCallback(result);
    });
  }

  closeDialog(success: boolean) {
    this.dialogRef.close({success: success});
  }

  private createForwardingHtmlForm() {
    return new ForwardingNgFormRef({
      formFn: () => {
        return this.form;
      }
    });
  }

  hasLocalFieldError(formControlName?: string, errorCode?: string): boolean {
    return this.formGroupValidationErrors.hasFieldError(formControlName, errorCode);
  }

  private createFormGroup(fb: FormBuilder): FormGroup {
    return fb.group({
      paymentType: [
        [],
        Validators.required
      ],
      currencyCode: [
        [],
        Validators.required
      ],
      journalCode: [
        [],
        Validators.required
      ],
      issueDatePicker: [
        [],
        Validators.required
      ],
      deliveryDatePicker: [
        [],
        Validators.required
      ],
      deadlineOffset: [
        [],
        Validators.required
      ],
    });
  }

  generateXml() {
    if (this.hasLocalFieldError()) {
      return;
    }
    this.exportInProgress = true;
    this.taskRecordService.exportSupCreate(this.getRequest()).subscribe((res: DownloadedFile) => {
        saveAs(res.getBlob(), res.getFileName('task_record_sup_create.xml'));
        this.closeDialog(true);
        this.exportInProgress = false;
      },
      error => {
        this.exportInProgress = false;
      }
    );
  }

  apiExport() {
    if (this.hasLocalFieldError()) {
      return;
    }
    this.exportInProgress = true;
    this.taskRecordService.supApiExport(this.getRequest()).subscribe((res: TaskRecord.SupApiExportResult) => {
        this.showToast(res);
        this.closeDialog(true);
        this.exportInProgress = false;
      },
      error => {
        this.exportInProgress = false;
      }
    );
  }

  showToast(result: TaskRecord.SupApiExportResult) {
    this.toasterService.clear();
    let toastType: string = UiConstants.toastTypeInfo;
    const toastTitleKey: string = 'COMMON_BUTTON_EXTERNAL_EXPORT';
    let toastMessageKey: string = 'COMMON_BUTTON_EXTERNAL_EXPORT';
    if (result.invalidTaskRecords.length > 0 && result.validTaskRecords.length === 0) {
      toastType = UiConstants.toastTypeError;
      toastMessageKey = 'TASK_RECORD_SUP_EXPORT_ERROR';
    } else if (result.validTaskRecords.length > 0 && result.invalidTaskRecords.length === 0) {
      toastType = UiConstants.toastTypeSuccess;
      toastMessageKey = 'TASK_RECORD_SUP_EXPORT_SUCCESS';
    } else {
      toastType = UiConstants.toastTypeWarning;
      toastMessageKey = 'TASK_RECORD_SUP_EXPORT_WARNING';
    }
    this.translateService.get([toastTitleKey, toastMessageKey],
      {
        validIds: result.validTaskRecords.map(a => a.externalId).join(', '),
        invalidIds: result.invalidTaskRecords.map(a => a.externalId).join(', ')
      }).subscribe((o) => {
      this.toasterService.pop({
        type: toastType,
        timeout: UiConstants.ToastTimeoutLong,
        title: TranslateUtils.extractValueFromObject(o, toastTitleKey),
        body: TranslateUtils.extractValueFromObject(o, toastMessageKey)
      });
    });

  }

  submit() {
    switch (this.data.type) {
      case TaskRecordSupExportDialogType.XML:
        this.generateXml();
        break;
      case TaskRecordSupExportDialogType.API:
        this.apiExport();
        break;
    }
  }

  private getRequest() {
    return {
      taskRecordIds: this.data.taskRecordIds,
      paymentType: this.model.paymentType[0]!.id,
      journalCode: this.model.journalCode,
      currencyCode: this.model.currencyCode[0]!.id,
      deadlineOffset: this.model.deadlineOffset,
      deliveryDate: this.datePickerParserFormatter.toLocalDate(this.model.deliveryDate)!,
      issueDate: this.datePickerParserFormatter.toLocalDate(this.model.issueDate)!
    };
  }

  getButtonLabel() {
    return this.data.type === TaskRecordSupExportDialogType.XML ? 'COMMON_BUTTON_DOWNLOAD' : 'COMMON_BUTTON_EXPORT';
  }
}

export class TaskRecordSupExportDialogData {
  type: TaskRecordSupExportDialogType;
  taskRecordIds: Set<number>;
}

export enum TaskRecordSupExportDialogType {
  XML, API
}

export class TaskRecordSupExportDialogResult {
  success: boolean;
}

class TaskRecordSupExportModel {
  paymentType: InvoicePaymentTypeMultiItem[] = [];
  journalCode: string = '401';
  currencyCode: MultiselectOptionItem<string>[];
  deadlineOffset: number = 0;
  issueDate: NgbDateStruct | null = null;
  deliveryDate: NgbDateStruct | null = null;
}
